import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import DialogContainer from 'components/common/Dialog/DialogContainer';
import ReferralShareCard from 'components/ReferralShareCard';
import {
    LiffShareButton,
    CopyShareButton,
    OtherShareButton,
    FacebookShareButton,
    TwitterShareButton,
    WhatsAppShareButton
} from 'client/components/Result/Share/button';
import useScrollElementOnMount from 'client/hooks/useScrollElementOnMount';
import { getDescription } from 'editor/game_mgmShare/utils';
import { defaultReferralShareCard } from 'config/promoDefaultSettingValue';
import { SHARE_CHANNEL_ORDER } from 'config/promoDefaultSettingValue';
import { stripHtmlTags } from 'utils/manipulateString';

// 參考邀請模組的邀請卡
/**
 * @method DialogReferralShareCard - 分享邀請卡 Dialog
 * @param {Object}   props
 * @param {String}   props.variant                 - 卡片類型 outlined | contained
 * @param {Boolean}  props.open                    - dialog 開關
 * @param {Boolean}  props.isFixedWindow           - 滿版 dialog (撐滿整個螢幕)
 * @param {Function} props.onClose                 - 按鈕 icon 隔壁的文字
 * @param {String}   props.dialogTextColor         - 按鈕 icon 隔壁的文字顏色
 * @param {String}   props.dialogBgColor           - 按鈕主題顏色 (背景顏色)
 * @param {Object}   props.referralShareCardConfig - 邀請卡設定 (title, description, imageUrl, button ,shareChannel,resultImageType,mediaData)
 * @param {Boolean}  props.isShowImage             - 顯示邀請卡圖片
 * @param {Boolean}  props.isShowReferralCode      - 顯示邀請碼
 * @param {String}   props.referralCode            - 邀請碼
 * @param {String}   props.referralLink            - 邀請碼連結
 * @param {Boolean}  props.isIgGame                - 是否為 IG 遊戲
 * @param {Boolean}  props.isEditor                - 是否為編輯器
 * @param {Boolean}  props.isFetchingData          - 是否正在取得資料
 * @param {Object}   props.style                   - 自定義 style
 * @returns {JSX.Element}
 */
const DialogReferralShareCard = ({
    variant,
    open,
    isFixedWindow,
    onClose,
    dialogTextColor,
    dialogBgColor,
    backdropColor,
    referralShareCardConfig: {
        title,
        description,
        imageUrl,
        button,
        shareChannel = [],
        resultImageType,
        mediaData,
        serialNo
    },
    isShowImage,
    isShowReferralCode,
    referralCode,
    referralLink,
    isFetchingData,
    isEditor,
    isIgGame,
    style,
    isPortfolioShare
}) => {
    // [860t12ray] 讓 promo 活動在作為 iframe 被嵌入在 page 的 情況下， dialog 打開時，要自動 scroll 到 promo 中間，讓 dialog 置中
    const bindRef = useScrollElementOnMount({ trigger: open });
    const channels =
        Object.keys(shareChannel).length > 0
            ? shareChannel
            : defaultReferralShareCard().referralShareCard?.shareChannel;
    // 處理票選模組 description 中的 HTML 標籤
    const displayDescription = stripHtmlTags(description);

    const buttonComponentMap = {
        LINE: () => (
            <LiffShareButton
                url={referralLink}
                color={dialogTextColor}
                variant={variant}
                isFetchingData={isFetchingData}
                isEditor={isEditor}
                title={title}
                description={description}
                buttonConfig={button}
                isPortfolioShare={isPortfolioShare}
                serialNo={serialNo}
            />
        ),
        FACEBOOK: () => (
            <FacebookShareButton
                url={referralLink}
                color={dialogTextColor}
                variant={variant}
                isFetchingData={isFetchingData}
                isEditor={isEditor}
            />
        ),
        COPY: () => (
            <CopyShareButton
                url={getDescription(
                    { invitationDesc: displayDescription, invitationTitle: title },
                    isShowReferralCode ? referralCode : '',
                    referralLink
                )}
                color={dialogTextColor}
                variant={'outlined'}
                isFetchingData={isFetchingData}
                isEditor={isEditor}
            />
        ),
        OTHER: () => (
            <OtherShareButton
                url={referralLink}
                title={title}
                text={getDescription(
                    { invitationDesc: displayDescription, invitationTitle: title },
                    isShowReferralCode ? referralCode : '',
                    referralLink,
                    true
                )}
                color={dialogTextColor}
                variant={'outlined'}
                isFetchingData={isFetchingData}
            />
        ),
        WHATSAPP: () => (
            <WhatsAppShareButton
                url={referralLink}
                color={dialogTextColor}
                isFetchingData={isFetchingData}
                isEditor={isEditor}
            />
        ),
        X: () => (
            <TwitterShareButton
                url={referralLink}
                color={dialogTextColor}
                variant={variant}
                isFetchingData={isFetchingData}
                isEditor={isEditor}
            />
        )
    };

    return (
        <DialogContainer
            open={open}
            isFixedWindow={isFixedWindow}
            disableBackdrop={false}
            onClose={onClose}
            themeTextColor={dialogTextColor}
            themeBgColor={dialogBgColor}
            backdropColor={backdropColor}
            ref={bindRef}
            style={[dialogContainerStyle, style]}
        >
            <div css={contentContainerStyle}>
                <ReferralShareCard
                    variant={variant}
                    type={'default'}
                    title={title}
                    description={displayDescription}
                    isShowImage={isShowImage}
                    imageUrl={imageUrl}
                    buttonConfig={button}
                    isShowReferralCode={isShowReferralCode}
                    referralCode={referralCode || '-'}
                    resultImageType={resultImageType}
                    isEditor={isEditor}
                    mediaData={mediaData}
                    isIgGame={isIgGame}
                />
            </div>
            <div css={footerContainerStyle}>
                {SHARE_CHANNEL_ORDER.filter(channelKey => channels[channelKey]).map(channelKey => {
                    const ButtonComponent = buttonComponentMap[channelKey];
                    return ButtonComponent ? (
                        <div css={buttonWrapper} key={channelKey}>
                            <ButtonComponent />
                        </div>
                    ) : null;
                })}
            </div>
        </DialogContainer>
    );
};

export default DialogReferralShareCard;

const contentContainerStyle = css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
`;

const footerContainerStyle = css`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    margin-bottom: 8px;
    gap: 16px 8px;
    flex-wrap: wrap;
`;

const dialogContainerStyle = css`
    border-radius: 24px;
`;

const buttonWrapper = css`
    width: 65px;
    min-width: 65px;
`;

DialogReferralShareCard.propTypes = {
    variant: PropTypes.oneOf(['outlined', 'contained']),
    open: PropTypes.bool,
    isFixedWindow: PropTypes.bool,
    onClose: PropTypes.func,
    dialogTextColor: PropTypes.string,
    dialogBgColor: PropTypes.string,
    backdropColor: PropTypes.string,
    enableLineLiff: PropTypes.bool,
    referralShareCardConfig: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        imageUrl: PropTypes.string,
        button: PropTypes.shape({
            enableDisplay: PropTypes.bool,
            label: PropTypes.string,
            uri: PropTypes.string,
            textColor: PropTypes.string,
            bgColor: PropTypes.string
        })
    }),
    isShowImage: PropTypes.bool,
    isShowReferralCode: PropTypes.bool,
    referralCode: PropTypes.string,
    referralLink: PropTypes.string
};
